import React, { useEffect, useMemo, useRef, useState } from "react";
import { callBackendApiGet } from "../../lib/api";
import logDrtEvent from "../../lib/logging";

function toMedName(item) {
    if (typeof item === "string") return item;
    if (item && typeof item === "object") {
        return (
            item.name ||
            item.genericName ||
            item.brandName ||
            item.label ||
            item.title ||
            item.value ||
            ""
        );
    }
    return "";
}

const OtherMedsQuestion = ({ onSubmit, onNext, initialValue, authToken = null }) => {
    const [yesNo, setYesNo] = useState(initialValue?.has ?? "");
    const [input, setInput] = useState("");
    const [meds, setMeds] = useState(Array.isArray(initialValue?.meds) ? initialValue.meds : []);
    const [, setResults] = useState([]);
    const [, setLoading] = useState(false);
    const [error, setError] = useState("");

    const activeReqRef = useRef(null);
    const inputRef = useRef(null);

    // Delayed autofocus when Yes is selected
    useEffect(() => {
        if (yesNo === "y") {
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [yesNo]);

    const debouncedSearch = useMemo(() => {
        let t;
        return (term) => {
            clearTimeout(t);
            t = setTimeout(async () => {
                const reqToken = Symbol();
                activeReqRef.current = reqToken;

                const q = term?.trim() ?? "";
                if (q.length < 2) {
                    if (activeReqRef.current === reqToken) setResults([]);
                    return;
                }

                setLoading(true);
                try {
                    const resp = await callBackendApiGet("/medications", { search: q }, authToken);

                    if (!resp.ok) {
                        logDrtEvent(`[conv] Medication search failed with status: ${resp.status}`);
                        if (activeReqRef.current === reqToken) setResults([]);
                        return;
                    }

                    const data = await resp.json().catch(() => ({}));
                    const incoming = Array.isArray(data?.medications) ? data.medications : [];

                    const normalized = incoming
                        .map(toMedName)
                        .filter(Boolean);

                    if (activeReqRef.current === reqToken) {
                        setResults(normalized);
                    }
                } catch (err) {
                    logDrtEvent(`[conv] Medication search error: ${err}`);
                    if (activeReqRef.current === reqToken) setResults([]);
                } finally {
                    if (activeReqRef.current === reqToken) setLoading(false);
                }
            }, 300);
        };
    }, [authToken]);

    useEffect(() => {
        debouncedSearch(input);
    }, [input, debouncedSearch]);

    const addMed = (name) => {
        if (!name) return;
        if (!meds.includes(name)) setMeds((m) => [...m, name]);
        setInput("");
        setResults([]);
        setError("");
    };

    const removeMed = (name) => {
        setMeds((m) => m.filter((x) => x !== name));
    };

    const handleContinue = (e) => {
        e.preventDefault();
        if (!yesNo) return;

        if (yesNo === "y" && meds.length === 0) {
            setError("Please add at least one medication or choose No.");
            return;
        }

        onSubmit({ has: yesNo, meds });
        onNext();

        // reset
        setYesNo("");
        setInput("");
        setMeds([]);
        setResults([]);
        setLoading(false);
        setError("");
    };

    return (
        <form onSubmit={handleContinue} className="input-form">
            <div className="multiselect-options-container" style={{ maxWidth: "36rem" }}>
                {/* Yes/No buttons */}
                <div className="treatment-options" style={{ justifyContent: "center", marginBottom: "0.5rem" }}>
                    <button
                        type="button"
                        className={`treatment-button ${yesNo === "y" ? "treatment-button-selected" : ""}`}
                        onClick={(e) => { e.currentTarget.blur(); setYesNo("y"); }}
                    >
                        Yes
                    </button>
                    <button
                        type="button"
                        className={`treatment-button ${yesNo === "n" ? "treatment-button-selected" : ""}`}
                        onClick={(e) => { e.currentTarget.blur(); setYesNo("n"); setMeds([]); setError(""); }}
                    >
                        No
                    </button>
                </div>

                {/* Input + search results */}
                {yesNo === "y" && (
                    <>
                        <p className="input-hint" style={{ marginTop: "0.5rem" }}>
                            Start typing to search common medications, then select to add.
                        </p>

                        <div className="input-field-container" style={{ marginTop: "0.25rem" }}>
                            <textarea
                                className="input-field"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="e.g., Metformin, Lisinopril, Sertraline"
                                ref={inputRef}
                                rows={2}
                            />
                            <button
                                type="button"
                                className="send-button"
                                disabled={!input?.trim()}
                                onClick={() => addMed(input?.trim())}
                            >
                                Add +
                            </button>
                        </div>

                        {/* {(loading || results.length > 0) && (
                            <div className="address-suggestions">
                                {loading && (
                                    <div className="address-suggestion-item" style={{ cursor: "default" }}>
                                        Searching…
                                    </div>
                                )}
                                {!loading &&
                                    results.slice(0, 12).map((name, idx) => (
                                        <div
                                            key={`${name}-${idx}`}
                                            className="address-suggestion-item"
                                            onClick={() => addMed(name)}
                                        >
                                            <div className="suggestion-main-text">{name}</div>
                                        </div>
                                    ))
                                }
                            </div>
                        )} */}

                        {/* selected meds */}
                        {meds.length > 0 && (
                            <div className="photo-summary-grid" style={{ marginTop: "0.75rem" }}>
                                {meds.map((m) => (
                                    <div key={m} className="treatment-button treatment-button-selected" style={{ display: "inline-flex", gap: ".5rem" }}>
                                        <span>{m}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeMed(m)}
                                            className="photo-remove-button"
                                            style={{ position: "static", width: "1.25rem", height: "1.25rem" }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {error && <div className="error-message">{error}</div>}
                    </>
                )}

                <button
                    type="submit"
                    className="multiselect-submit-button"
                    disabled={!yesNo || (yesNo === "y" && meds.length === 0)}
                >
                    Continue
                </button>
            </div>
        </form>
    );
};

export default OtherMedsQuestion;
